import { NavLink } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { countNewLeads, countPendingOnboard } from "../lib/api";

interface Props {
  children: ReactNode;
  onLogout: () => void;
}

/* IA 四組收斂（2026-10-19 方案 docs/ui-ia-revamp-plan-20261019.md §6）：
   /admin＝平台方控制台——只管「租戶與訂閱」與平台配置，不做租戶級功能操作。
   試用額度併入租戶編輯頁、語音會議記錄搬去 /merchant（路由保留至內容手術完成）。 */
const NAV_SECTIONS: { title: string; items: { to: string; label: string }[] }[] = [
  {
    title: "📋 營運",
    items: [
      { to: "/onboarding", label: "🧾 開通中心" },
      { to: "/tenants", label: "🏢 租戶列表" },
      { to: "/ops", label: "📈 營運透視" },
    ],
  },
  {
    title: "📣 增長",
    items: [
      { to: "/marketing-assets", label: "📣 行銷中心" },
      { to: "/tracking", label: "🎯 推廣追蹤" },
      { to: "/referrals", label: "🎁 推薦計劃" },
      { to: "/commerce", label: "🛍️ 多品牌銷售" },
      { to: "/partners", label: "🤝 戰略夥伴" },
      { to: "/leads", label: "🔥 增值線索" },
      { to: "/analyst", label: "🧠 AI 員工" },
    ],
  },
  {
    title: "🛡️ 品質與風控",
    items: [
      { to: "/kb-learning", label: "🧠 AI 學習審核" },
      { to: "/chat-logs", label: "💬 對話監察" },
      { to: "/anti-abuse", label: "🛡️ 反濫用上限" },
      { to: "/voice-assistant", label: "🎙️ 語音服務運維台" },
    ],
  },
  {
    title: "⚙️ 平台配置",
    items: [
      { to: "/system", label: "⚙️ 系統資訊" },
      { to: "/outbound", label: "📤 主動發送" },
      { to: "/engine-rules", label: "⚡ 引擎規則" },
      { to: "/templates", label: "📨 訊息模版" },
      { to: "/knowledge", label: "📝 知識庫管理" },
    ],
  },
];

export default function Layout({ children, onLogout }: Props) {
  // 增值線索閃光點：new 狀態線索未點入睇過就長期閃（30 秒輪詢；點入睇完歸零自動停）
  const [newLeads, setNewLeads] = useState(0);
  // 開通待辦 badge（線 1）：awaiting_provision 單數——SLA 2 小時，唔可以滯留
  const [pendingOnboard, setPendingOnboard] = useState(0);
  // 明亮 Mode（2026-09-12）：預設暗黑；與商戶面各頁共用 localStorage key aibot_theme
  const [light, setLight] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("light")
  );
  // W-04（2026-10-18）：小屏側欄＝抽屜——固定 256px 側欄令 375px 主區只剩 119px，
  // 撤銷／開關按鈕被擠出視窗外（element outside viewport／攔截 pointer events）
  const [navOpen, setNavOpen] = useState(false);

  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains("light");
    document.documentElement.classList.toggle("light", next);
    try {
      localStorage.setItem("aibot_theme", next ? "light" : "dark");
    } catch { /* ignore */ }
    setLight(next);
  };

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      if (!localStorage.getItem("admin_key")) return;
      const [leads, pending] = await Promise.all([countNewLeads(), countPendingOnboard()]);
      if (!alive) return;
      setNewLeads(leads);
      setPendingOnboard(pending);
    };
    poll();
    const timer = setInterval(poll, 30000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  return (
    /* 2026-09-10（Captain 指示）：h-screen＋overflow-hidden＝側欄（連品牌區）固定，
       長內容只有主內容區捲動（main 已有 overflow-auto） */
    <div className="h-screen overflow-hidden flex bg-slate-950">
      {/* Sidebar：md+＝固定側欄；小屏＝抽屜（W-04）＋背景遮罩 */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 border-r border-slate-800 flex flex-col transform transition-transform duration-200 md:static md:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-emerald-400">AI Bot Admin</h2>
            <p className="text-xs text-slate-500 mt-1 font-mono">
              wts<span className="text-emerald-500/80">aibot</span>.com/admin
            </p>
          </div>
          <button
            onClick={() => setNavOpen(false)}
            className="md:hidden ml-2 p-2 text-slate-400 hover:text-slate-100 rounded-lg"
            aria-label="關閉導覽"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="px-4 pb-1 pt-2 text-[10px] font-bold tracking-widest text-slate-600 select-none">
                {section.title}
              </div>
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-emerald-600/20 text-emerald-400 border border-emerald-600/30"
                        : "text-slate-300 hover:bg-slate-800 hover:text-slate-100 border border-transparent"
                    }`
                  }
                >
                  <span>{item.label}</span>
                  {item.to === "/leads" && newLeads > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold animate-pulse">
                      {newLeads}
                    </span>
                  )}
                  {item.to === "/onboarding" && pendingOnboard > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-slate-950 text-[11px] font-bold animate-pulse">
                      {pendingOnboard}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800 space-y-1">
          <button
            onClick={toggleTheme}
            className="w-full px-4 py-2 text-sm text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition-colors text-left"
          >
            {light ? "🌙 暗黑 Mode" : "☀️ 明亮 Mode"}
          </button>
          <button
            onClick={onLogout}
            className="w-full px-4 py-2 text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors text-left"
          >
            🚪 登出
          </button>
        </div>
      </aside>

      {/* Main Content：min-w-0（W-04）＝flex 子項收縮基線，防止內容把主區撐爆 */}
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="md:hidden sticky top-0 z-20 bg-slate-950/95 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setNavOpen(true)}
            className="p-2 -ml-2 text-slate-300 hover:text-slate-100 hover:bg-slate-800 rounded-lg"
            aria-label="打開導覽"
          >
            ☰
          </button>
          <span className="text-sm font-bold text-emerald-400">AI Bot Admin</span>
        </div>
        <div className="w-full max-w-[1800px] mx-auto p-4 md:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
